import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated/Modal'
import { DetectBlur } from '@/containers/util-containers'
import type { LeaderGroupForm } from '../types'
import { Form } from '@/components_deprecated/Form'
import styled from 'styled-components'
import { Table } from '@/components_deprecated/Table'
import { useMemo, useState } from 'react'
import { Icon } from '@ur-solutions/react-components'
import { Input, UserSelect } from '@/components_deprecated'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import { SalaryGroupSelect } from '@/components_deprecated/Select/SalaryGroupSelect'

interface FieldProps {
  alignLeft?: boolean
  alignRight?: boolean
  area?: string
}
export const Field = styled.div<FieldProps>`
  grid-area: ${props => props.area};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${props => props.alignLeft && 'align-items: flex-start;'}
  ${props => props.alignRight && 'align-items: flex-end;'}

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const StyledForm = styled(Form)`
  gap: 0.5rem;
`

const DoubleField = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
`

const StyledTable = styled(Table)`
  max-height: 300px;
  overflow-y: auto;
`

interface EditLeaderGroupPromptProps {
  initialData?: LeaderGroupForm
  companyUsers: {
    id: string
    fullName: string
  }[]
  onSubmit: (data: LeaderGroupForm | null) => void
}

export const EditLeaderGroupPrompt: React.FC<EditLeaderGroupPromptProps> = ({
  initialData,
  companyUsers,
  onSubmit,
}) => {
  const { formValues, formChangeHandler, formValid } = useForm<LeaderGroupForm>(
    {
      values: initialData ?? {
        name: '',
        leaders: [],
        users: [],
        salaryGroup: null,
      },
      config: {
        initAsInvalid: true,
      },
      validators: {
        name: () => {
          if (!formValues.name) {
            return translations.error.required
          }
          return null
        },
      },
    }
  )

  const [userSearch, setUserSearch] = useState('')

  const translations = useTranslate({
    label: {
      name: 'common.name',
      remove: 'common.remove',
      search: 'common.search',
      addUsers: 'common.add-users',
      responsibleUsers: 'common.responsible-users',
      salaryGroup: 'salary.salary-group',
    },
    create: 'common.create-alt',
    edit: 'common.edit',
    update: 'common.update',
    users: 'common.users',

    error: {
      required: 'common.required',
    },
  })

  const selectedUsers = useMemo(
    () =>
      companyUsers.filter(
        user =>
          formValues.users.includes(user.id) &&
          user.fullName.includes(userSearch)
      ),
    [companyUsers, formValues.users, userSearch]
  )

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={initialData ? translations.edit : translations.create}
        submitText={translations.update}
        submitDisabled={!formValid}
        onCancel={() => onSubmit(null)}
        onSubmit={() => onSubmit(formValues)}
        width="600px"
      >
        <StyledForm onSubmit={() => onSubmit(formValues)}>
          <Field>
            <label>{translations.label.name}</label>
            <Input
              value={formValues.name}
              onChange={formChangeHandler('name')}
              grayed
            />
          </Field>
          <Field>
            <label>{translations.label.responsibleUsers}</label>
            <SelectMultipleUsers
              selected={formValues.leaders}
              onChange={formChangeHandler('leaders')}
              grayed
            />
          </Field>
          <Field>
            <label>{translations.label.salaryGroup}</label>
            <SalaryGroupSelect
              value={formValues.salaryGroup}
              onChange={formChangeHandler('salaryGroup')}
              grayed
              nullable
            />
          </Field>
          <DoubleField>
            <Field>
              <label>{translations.label.addUsers}</label>
              <UserSelect
                value={null}
                exclude={selectedUsers.map(user => user.id)}
                onChange={val =>
                  val
                    ? formChangeHandler('users')(
                        formValues.users.includes(val)
                          ? formValues.users.filter(id => id !== val)
                          : [...formValues.users, val]
                      )
                    : void 0
                }
                grayed
              />
            </Field>

            <Field>
              <label>{translations.label.search}</label>
              <Input
                value={userSearch}
                onChange={val => setUserSearch(val)}
                grayed
                placeholder={translations.label.search}
              />
            </Field>
          </DoubleField>

          <StyledTable headerMargin noData={formValues.users.length === 0}>
            <thead>
              <tr>
                <th>{translations.label.name}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {selectedUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.fullName}</td>
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() =>
                        formChangeHandler('users')(
                          formValues.users.filter(id => id !== user.id)
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </StyledForm>
      </RegularModal>
    </DetectBlur>
  )
}
